/**
 * The selectable week range for a WeekStepper.
 *
 * This used to be "today through the last Monday of the current calendar year",
 * which meant the forward range shrank as the year ran out and vanished in late
 * December — a project's start date simply could not be pushed into next year.
 * The window is now relative to the date being edited rather than to a calendar
 * boundary, so there is always at least a year of runway ahead of it.
 *
 * `today` is a required parameter, not a `new Date()` default: a clock the caller
 * can't control is exactly how the year-end bug stayed invisible in tests.
 */
import { addWeeks, startOfWeek } from "@/utils/dateUtils";

/** How far either side of the anchor a stepper reaches when no bound is given. */
export const WEEK_STEPPER_FORWARD_WEEKS = 52;
export const WEEK_STEPPER_BACKWARD_WEEKS = 52;

export interface WeekStepperBoundsInput {
  /**
   * The week the window is anchored to — the value as of when this editing
   * session began, NOT the live value. Anchoring to the live value would let the
   * ceiling advance with every pick, which is the endless scroll we're avoiding:
   * pushing a project out another year takes a second, deliberate edit.
   */
  anchor: Date;
  /**
   * Caller's floor (e.g. a project's "not before this week", or an end date's
   * "start + 2 weeks"). Left out, the window is symmetric: a year either side of
   * the anchor. "You can't schedule in the past" is a project policy, so the
   * project editors pass it explicitly rather than every week picker inheriting
   * it — a view control like the board's "Scroll to date" needs to look back.
   */
  minDate?: Date | null;
  /** Caller's ceiling. When given it replaces the +52 week default outright. */
  maxDate?: Date | null;
  /** The current real date. Required. */
  today: Date;
}

export interface WeekStepperBounds {
  /** Earliest selectable week (Monday, UTC). */
  floor: Date;
  /** Latest selectable week (Monday, UTC). */
  ceiling: Date;
}

const earlier = (a: Date, b: Date): Date => (a.getTime() <= b.getTime() ? a : b);
const later = (a: Date, b: Date): Date => (a.getTime() >= b.getTime() ? a : b);

/**
 * Resolve the floor and ceiling for one editing session.
 *
 * Floor: with a `minDate`, that date or this week, whichever is later — but never
 * later than the anchor itself. A list must always contain the value it's showing,
 * so a project already under way can still see (and return to) its own start date,
 * while a project starting this week shows no earlier weeks at all. With no
 * `minDate`, 52 weeks before the anchor.
 *
 * Ceiling: the caller's `maxDate`, else 52 weeks past the anchor.
 */
export function weekStepperBounds({
  anchor,
  minDate,
  maxDate,
  today,
}: WeekStepperBoundsInput): WeekStepperBounds {
  const anchorWeek = startOfWeek(anchor);
  const currentWeek = startOfWeek(today);

  // A caller's floor never reaches into the past, whatever date it names...
  const requestedFloor = minDate
    ? later(startOfWeek(minDate), currentWeek)
    : addWeeks(anchorWeek, -WEEK_STEPPER_BACKWARD_WEEKS);
  // ...unless the value being edited is already back there.
  const floor = earlier(anchorWeek, requestedFloor);

  const requestedCeiling = maxDate
    ? startOfWeek(maxDate)
    : addWeeks(anchorWeek, WEEK_STEPPER_FORWARD_WEEKS);
  // A caller-supplied max that lands below the floor would invert the range.
  const ceiling = later(requestedCeiling, floor);

  return { floor, ceiling };
}

/** Whole weeks from `from` to `to`; negative when `to` precedes `from`. */
export function weeksBetween(from: Date, to: Date): number {
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  return Math.round((startOfWeek(to).getTime() - startOfWeek(from).getTime()) / MS_PER_WEEK);
}
